"use client";

import {
	Badge,
	BadgeProps,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	FeaturedText,
	type FeaturedTextProps,
	Heading,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Text
} from "@pamperedchef/design-system";
import { className } from "@/app/utilities/className";
import { objectHasValues } from "@/app/utilities/objectHasValues";

import {
	ContactYourConsultant,
	type ContactYourConsultantProps
} from "../ContactYourConsultant";
import * as styles from "./MeetYourConsultant.module.scss";

interface MeetYourConsultantProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	consultantNotChosen?: {
		headline: FeaturedTextProps;
		eyebrow: string;
		title: string;
		description: string;
		cta: LinkProps<LinkComponentProps>;
	};
	consultant?: {
		headline: FeaturedTextProps;
		photo: ImageProps<ImageComponentProps>;
		name: string;
		position: string;
		readFullBio: LinkProps<LinkComponentProps>;
		bioTitle: string;
		bioDescription: string;
		contactYourConsultant: ContactYourConsultantProps;
	};
	cards: {
		title: string;
		description: string;
		badges?: BadgeProps[];
		cta?: LinkProps<LinkComponentProps>;
	}[];
}

/**
 * Meet Your Consultant Block
 */
export function MeetYourConsultant<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>(
	props: MeetYourConsultantProps<LinkComponentProps, ImageComponentProps>
): JSX.Element {
	return (
		<section
			{...className(
				styles["meet-your-consultant"],
				props.consultant
					? styles["meet-your-consultant--consultant"]
					: styles["meet-your-consultant--not-chosen"]
			)}
		>
			<Container containerClassName={styles["container"]}>
				{props.consultantNotChosen && (
					<>
						<FeaturedText
							{...props.consultantNotChosen.headline}
							alignment="left"
							className={styles["headline"]}
						/>
						<div className={styles["info"]}>
							<Text
								tag="span"
								styledAs="xsb"
								className={styles["eyebrow"]}
							>
								{props.consultantNotChosen.eyebrow}
							</Text>
							<Heading
								tag="h3"
								styledAs="h2"
								className={styles["title"]}
							>
								{props.consultantNotChosen.title}
							</Heading>
							<Text
								styledAs="xsr"
								className={styles["description"]}
							>
								{props.consultantNotChosen.description}
							</Text>
							{objectHasValues(props.consultantNotChosen.cta) && (
								<Link<LinkComponentProps>
									{...props.consultantNotChosen.cta}
									className={styles["cta"]}
									kind="primary"
								/>
							)}
						</div>
					</>
				)}
				{props.consultant && (
					<>
						<div className={styles["consultant"]}>
							<FeaturedText
								{...props.consultant.headline}
								alignment="left"
								className={styles["headline"]}
							/>
							<Image<ImageComponentProps>
								{...props.consultant.photo}
								className={styles["photo"]}
								alt={props.consultant.photo.alt || ""}
							/>
							<Heading
								styledAs="mdr"
								tag="h2"
								{...className(
									styles["consultant-name"],
									"mb-0"
								)}
							>
								{props.consultant.name}
							</Heading>
							<Text
								styledAs="xsr"
								{...className(
									styles["consultant-position"],
									"mb-0"
								)}
							>
								{props.consultant.position}
							</Text>
							<div className={styles["ctas"]}>
								<ContactYourConsultant
									{...props.consultant.contactYourConsultant}
								/>
								{objectHasValues(
									props.consultant.readFullBio
								) && (
									<Link<LinkComponentProps>
										{...props.consultant.readFullBio}
										kind="tertiary"
									/>
								)}
							</div>
						</div>
						<div className={styles["bio"]}>
							<Heading
								styledAs="h3"
								tag="h2"
								className={styles["bio-title"]}
							>
								{props.consultant.bioTitle}
							</Heading>
							<Text
								styledAs="xsr"
								className={styles["bio-description"]}
							>
								{props.consultant.bioDescription}
							</Text>
						</div>
					</>
				)}
				{props.cards && props.cards.length > 0 && (
					<div className={styles["cards"]}>
						{props.cards.map((card, index) => (
							<article
								key={index}
								aria-labelledby={`card-${index}`}
								className={styles["card"]}
							>
								<Heading
									tag="h3"
									styledAs="h3"
									id={`card-${index}`}
									{...className(styles["card-title"], "mb-1")}
								>
									{card.title}
								</Heading>
								<Text
									styledAs="xsr"
									{...className(
										styles["card-description"],
										"mb-2"
									)}
								>
									{card.description}
								</Text>
								{card.badges && card.badges.length > 0 && (
									<div className="mb-4">
										{card.badges.map((badge, idx) => (
											<Badge
												key={idx}
												{...badge}
												kind="inverted"
											/>
										))}
									</div>
								)}
								{objectHasValues(card.cta) && card.cta && (
									<Link<LinkComponentProps>
										{...card.cta}
										kind="secondary"
										className={styles["card-cta"]}
									/>
								)}
							</article>
						))}
					</div>
				)}
			</Container>
		</section>
	);
}
